import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { enterContest, withdrawContestEntry } from "../../../utils/api";
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-hot-toast';

const useContestDetails = (contest) => {
  const navigate = useNavigate();
  const { userId } = useAuth(); // Get userId from Auth
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showEntrantsModal, setShowEntrantsModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [currentUsername, setCurrentUsername] = useState(null);

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    const username = localStorage.getItem("username");
    setIsLoggedIn(!!accessToken);
    setCurrentUsername(username);
  }, []);

  // Determine if the current user has entered this contest
  const isUserEntered = useMemo(() => {
    if (!userId || !contest?.entrants) return false;
    return contest.entrants.some(entrant => entrant.userId === userId);
  }, [userId, contest?.entrants]);

  const handleEnterContest = async () => {
    if (!isLoggedIn) {
      setModalContent("You must be logged in to enter contests.");
      setShowModal(true);
      return;
    }

    if (contest.draftType === 'Snake') {
      setIsSubmitting(true);
      try {
        await enterContest(contest._id, {}); 
        const toastId = toast.success('Successfully entered the contest! The draft will begin when the lobby is full.', {
          duration: 2000
        });
        // For snake drafts, don't refresh - the draftStarting event will handle navigation
        if (contest.entrants.length < contest.maxEntries - 1) {
          // Only refresh if we're not the last entrant
          setTimeout(() => {
            toast.dismiss(toastId);
            navigate(0);
          }, 2000);
        }
      } catch (error) {
        console.error("Failed to enter snake draft:", error);
        toast.error(error.message || 'An error occurred while entering the contest.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      navigate(`/draft/${contest._id}`);
    }
  };

  const handleWithdraw = async () => {
    if (!isUserEntered || contest.draftType !== 'Snake') return;

    // Find the specific entry for the user. For snake drafts, we assume one entry.
    const userEntry = contest.entrants.find(e => e.userId === userId);
    if (!userEntry) {
      toast.error("Could not find your entry to withdraw.");
      return;
    }

    setIsSubmitting(true);
    try {
      await withdrawContestEntry(contest._id, userEntry.entryName);
      const toastId = toast.success('Successfully withdrawn from the contest.', {
        duration: 2000
      });
      // Wait for toast to be visible and then refresh
      setTimeout(() => {
        toast.dismiss(toastId);
        navigate(0);
      }, 2000);
    } catch (error) {
      console.error("Failed to withdraw from snake draft:", error);
      toast.error(error.message || 'An error occurred while withdrawing.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewEntries = () => {
    if (isLoggedIn) {
      setShowEntrantsModal(true);
      setShowModal(false);
    } else {
      setModalContent("You must be logged in to view current entries.");
      setShowModal(true);
      setShowEntrantsModal(false);
    }
  };

  return {
    isSubmitting,
    isUserEntered,
    handleWithdraw,
    showModal,
    setShowModal,
    showEntrantsModal,
    setShowEntrantsModal,
    isLoggedIn,
    modalContent,
    currentUsername,
    handleEnterContest,
    handleViewEntries,
  };
};

export default useContestDetails;
